'use client';

import React, { useCallback } from 'react';
import styled from '@benzinga/themetron';

export interface ISliderProps<ObjectType> {
  multi?: boolean;
  defaultValue?: ObjectType;
  enableTooltips?: boolean;
  tipFormatter?: (value?: number) => React.ReactNode;
  min?: number;
  max?: number;
  label?: string;
  //onChange?: (value: ObjectType) => void;
  onChange?: (value: any) => void;
  onMouseUp?: (value: any) => void;
  step?: number;
  range?: number[];
}

export type SliderProps = ISliderProps<number | [number, number] | number[]>;

export const SliderDefaults = {
  FROM: 0,
  MAX: 100,
  MIN: 0,
  TO: 100,
};

export type SliderState = {
  from: number;
  to: number;
};

export type sliderLeftRightStyle = {
  left: string | number;
  right: string;
};

export const Slider: React.FC<SliderProps> = ({
  defaultValue,
  enableTooltips = false,
  label,
  max: maxProp,
  min: minProp,
  multi = false,
  onChange,
  onMouseUp,
  range,
  step = 1,
  tipFormatter,
}) => {
  const [from, setFrom] = React.useState(SliderDefaults.FROM);
  const [to, setTo] = React.useState(SliderDefaults.TO);
  const [showSliderValueTooltip, setShowSliderValueTooltip] = React.useState<'TO' | 'FROM' | null>(null);

  const getRangeLength = useCallback(() => {
    if (!Array.isArray(range)) {
      return 0;
    }

    return range.length;
  }, [range]);

  const min = getRangeLength() > 2 ? 0 : minProp || SliderDefaults.MIN;
  const max = getRangeLength() > 2 ? 100 : maxProp || SliderDefaults.MAX;
  const localStep = getRangeLength() > 2 ? 100 / (getRangeLength() - 1) : step;

  const getRangesMap = useCallback(() => {
    if (Array.isArray(range)) {
      const map: number[] = [];
      let counter = 0;

      const multiplier = Math.round(100 / (range.length - 1));
      range.forEach(() => {
        map.push(counter);
        counter = counter + multiplier;
      });

      return map;
    }

    return [];
  }, [range]);

  const getSliderValueByRange = useCallback(
    (value: number) => {
      if (Array.isArray(range) && range.length > 2) {
        const rangeMap = getRangesMap();

        return rangeMap[range.indexOf(value)];
      }

      return value;
    },
    [range, getRangesMap],
  );

  React.useEffect(() => {
    if (defaultValue === undefined) {
      setFrom(!isNaN(min) ? min : 0);
      setTo(!isNaN(max) ? max : 0);
    } else {
      if (Array.isArray(defaultValue)) {
        setFrom(getSliderValueByRange(defaultValue[0]));
        setTo(getSliderValueByRange(defaultValue[1]));
      } else {
        setTo(getSliderValueByRange(Number(defaultValue)));
      }
    }
  }, [defaultValue, max, min, getSliderValueByRange]);

  const handleChangeFrom = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!multi) return;
    const value = Number(e.target.value);
    if (value > to) return;
    showSliderValueTooltip !== 'FROM' && handleOnChangeValue('FROM');
    setFrom(value);

    if (range && getRangeLength() > 2) {
      const currentValue = getValueFromRange(value);
      const toValue = getValueFromRange(to);

      onChange && onChange([currentValue, toValue]);
    } else {
      onChange && onChange([value, to]);
    }
  };

  const getValueFromRange = useCallback(
    (value: number) => {
      if (range && getRangeLength() > 2) {
        if (value === max) {
          return range[range.length - 1];
        } else if (value === min) {
          return range[0];
        } else {
          const rangesMap = getRangesMap();
          const index = rangesMap.indexOf(value);

          return range[index];
        }
      }

      return 0;
    },
    [range, getRangesMap, max, min, getRangeLength],
  );

  const handleChangeTo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (value < from) return;
    showSliderValueTooltip !== 'TO' && handleOnChangeValue('TO');
    setTo(value);

    if (range && getRangeLength() > 2) {
      const fromValue = getValueFromRange(from);
      const currentValue = getValueFromRange(value);

      onChange && onChange(multi ? [fromValue, currentValue] : currentValue);
    } else {
      onChange && onChange(multi ? [from, value] : value);
    }
  };

  const handleOnChangeValue = (value: 'TO' | 'FROM') => {
    setShowSliderValueTooltip(value);
    setTimeout(() => setShowSliderValueTooltip(null), 1500);
  };

  const handleMouseUp = () => {
    if (onMouseUp) {
      onMouseUp((multi ? [from, to] : to) as number & [number, number]);
    }
  };

  const getTrackStyles = (min: number, max: number): sliderLeftRightStyle => {
    const diffMaxMin = max - min;
    const trackLeft = multi ? `${((from - min) * 100) / diffMaxMin}%` : 0;
    const trackRight = multi ? `${((max - to) * 100) / diffMaxMin}%` : `${100 - ((to - min) * 100) / diffMaxMin}%`;

    return {
      left: trackLeft,
      right: trackRight,
    };
  };

  const getMarkStyle = (min: number, max: number): sliderLeftRightStyle => {
    const diffMaxMin = max - min;
    const leftStyle = `${((from - min) * 100) / diffMaxMin}%`;
    const rightStyle = `${((to - min) * 100) / diffMaxMin}%`;

    return {
      left: leftStyle,
      right: rightStyle,
    };
  };

  const trackStyle = getTrackStyles(min, max);
  const markStyle = getMarkStyle(min, max);

  return (
    <Container className="slider">
      {label && (
        <label className="slider-label" htmlFor="slider-input">
          {label}
        </label>
      )}
      <div className="slider-wrapper">
        <div className="slider-rail">
          <div className="slider-track" style={{ left: trackStyle.left, right: trackStyle.right }}></div>
          {multi && (
            <span className="slider-mark" style={{ left: markStyle.left }}>
              {enableTooltips && showSliderValueTooltip === 'FROM' && (
                <div className="slider-tooltip">{tipFormatter ? tipFormatter(from) : from}</div>
              )}
            </span>
          )}
          <span className="slider-mark" style={{ left: markStyle.right }}>
            {enableTooltips && showSliderValueTooltip === 'TO' && (
              <div className="slider-tooltip">{tipFormatter ? tipFormatter(to) : to}</div>
            )}
          </span>
        </div>
        {multi && (
          <input
            id="slider-input"
            max={max}
            min={min}
            onChange={handleChangeFrom}
            onMouseUp={handleMouseUp}
            step={localStep}
            type="range"
            value={from}
          />
        )}
        <input
          max={max}
          min={min}
          onChange={handleChangeTo}
          onMouseUp={handleMouseUp}
          step={localStep}
          type="range"
          value={to}
        />
      </div>
    </Container>
  );
};

const Container = styled.div`
  &.slider {
    display: flex;
    flex-direction: column;
    padding: 13px 20px 8px;
    position: relative;
    width: 100%;
    padding: 5px 0;
    border-radius: 6px;
    touch-action: none;

    .slider-label {
      margin-bottom: 1rem;
      text-transform: uppercase;
      color: #736e7d;
      font-weight: 600;
      font-size: 13px;
    }

    .slider-wrapper {
      width: 100%;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;

      .slider-rail {
        position: absolute;
        left: 5px;
        right: 5px;
        height: 4px;
        background-color: ${({ theme }) => theme.colorPalette.gray100};
        mix-blend-mode: normal;
        border-radius: 3px;
      }

      .slider-track {
        position: absolute;
        left: 0;
        height: 4px;
        border-radius: 14px;
        background-color: ${({ theme }) => theme.colorPalette.blue500};
      }

      .slider-mark-container {
      }

      .slider-mark {
        position: absolute;
        top: -5px;
        z-index: 2;
        width: 14px;
        height: 14px;
        text-align: left;
        margin-left: -6px;
        cursor: pointer;
        background-color: ${({ theme }) => theme.colorPalette.white};
        border: 1px solid ${({ theme }) => theme.colorPalette.gray300};
        border-radius: ${({ theme }) => theme.borderRadius.full};
        outline: none;

        .slider-tooltip {
          transition-duration: 300ms;
          transition-property: opacity;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          color: ${({ theme }) => theme.colorPalette.white};
          background-color: #383838;
          box-shadow:
            0 3px 6px -4px rgb(0 0 0 / 12%),
            0 6px 16px 0 rgb(0 0 0 / 8%),
            0 9px 28px 8px rgb(0 0 0 / 5%);
          border-radius: ${({ theme }) => theme.borderRadius.default};
          font-size: ${({ theme }) => theme.fontSize.sm};

          border-radius: 0.5rem;
          display: inline-block;
          z-index: 5;
          text-align: center;
          line-height: 1.5;
          padding: 0 0.5rem;

          position: absolute;
          left: 50%;
          transform: translate(-50%, -50%);
          top: -20px;
        }
      }

      > input[type='range'] {
        position: absolute;
        pointer-events: none;
        -webkit-appearance: none;
        z-index: 3;
        height: 14px;
        width: 100%;
        opacity: 0;
        left: 0;
        cursor: pointer;
      }
      > input[type='range']:focus::-webkit-slider-runnable-track {
        background: transparent;
        border: transparent;
      }

      > input[type='range']:focus {
        outline: none;
      }

      > input[type='range']::-webkit-slider-thumb {
        pointer-events: all;
        width: 14px;
        height: 14px;
        border-radius: 0px;
        border: 0 none;
        -webkit-appearance: none;
      }

      > input[type='range']::-moz-range-thumb {
        pointer-events: all;
        width: 14px;
        height: 14px;
        border-radius: 0px;
        border: 0 none;
        -webkit-appearance: none;
      }

      > input[type='range']::-ms-fill-lower,
      > input[type='range']::-ms-fill-upper {
        background: transparent;
        border: 0 none;
      }

      > input[type='range']::-ms-tooltip {
        display: none;
      }
    }
  }
`;

export default Slider;
